const TAG = '[ZipAdBook:auth]';

// ─── Auth Request Counter ──────────────────────────────────────────────────
// Tracks total auth API calls in the current session for debugging.
// Check window.__authCallCount in browser console to see total count.
const incrementAuthCallCount = (method: string) => {
  if (typeof window === 'undefined') return;
  const w = window as any;
  w.__authCallCount = (w.__authCallCount || 0) + 1;
  w.__authCallLog = w.__authCallLog || [];
  w.__authCallLog.push({ method, t: new Date().toISOString(), count: w.__authCallCount });
};

export function maskEmail(email: string): string {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.indexOf('@');
  if (at <= 0) return '[invalid-email]';
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  if (!domain) return '[invalid-email]';
  const visible = local.length <= 2 ? `${local[0] ?? ''}…` : `${local.slice(0, 2)}…`;
  return `${visible}@${domain}`;
}

type AuthDebugPayload = Record<string, unknown>;

export function authDebug(event: string, payload?: AuthDebugPayload) {
  // Track API call counts for auth methods that hit the network
  const isApiCall =
    event.includes('getSession') ||
    event.includes('signIn:start') ||
    event.includes('signUp:start') ||
    event.includes('refreshSession:starting') ||
    event.includes('refreshTimer:firing') ||
    event.includes('initializeSession:fallback') ||
    event.includes('resendVerification') ||
    event.includes('getCurrentUser');

  if (isApiCall) {
    incrementAuthCallCount(event);
  }

  console.log(TAG, event, { t: new Date().toISOString(), ...payload });
}

export function authDebugError(event: string, err: unknown, payload?: AuthDebugPayload) {
  const error =
    err && typeof err === 'object'
      ? {
          message: 'message' in err ? String((err as { message: unknown }).message) : undefined,
          code: 'code' in err ? String((err as { code: unknown }).code) : undefined,
          status: 'status' in err ? (err as { status: unknown }).status : undefined,
        }
      : { message: String(err) };
  console.error(TAG, event, { t: new Date().toISOString(), ...payload, error });
}

// ─── Debug Helpers (browser console) ──────────────────────────────────────
// Usage in browser console:
//   window.__printAuthLog()   → prints all auth API calls with timestamps
//   window.__authCallCount    → total auth API call count
if (typeof window !== 'undefined') {
  (window as any).__printAuthLog = () => {
    const log = (window as any).__authCallLog || [];
    console.table(log);
    console.log(`Total auth API calls: ${(window as any).__authCallCount || 0}`);
  };
}
